import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { BookingDetailsAdditionalService } from '../../../../../store/slices/bookingCreationSlice';
import { AdditionalService } from '../../../../../types/bookingCreation';
import { useTranslation } from 'react-i18next';
import BookingDetailsServiceItem from './BookingDetailsServiceItem';
import { checkHasQuantity } from '../../../../../utils';

const BookingDetailsAdditionalServices = ({
    additionalServices,
    allAdditionalServices,
}: {
    additionalServices: BookingDetailsAdditionalService[];
    allAdditionalServices: AdditionalService[];
}) => {
    const { t } = useTranslation();

    const visibleServices = allAdditionalServices.filter((item) => item?.langNames?.en !== 'Internet');

    // Booked services that can't be matched into the asset's *current* catalog
    // (catalog empty/not returned, or the service was replaced since booking).
    // The booking data — not the live catalog — is the source of truth for what
    // was booked, so append them as pseudo-catalog entries and let them render
    // through the regular path, where checkHasQuantity marks them checked
    // (same defect class as the series detail page, #1441).
    const unmatchedBookedServices: AdditionalService[] = additionalServices
        .filter(
            (booked) =>
                booked?.langNames?.en !== 'Internet' &&
                !visibleServices.some((s) => s.id === booked.id),
        )
        .map((booked) => ({
            id: booked.id,
            assetId: '',
            serviceName: booked.serviceName,
            langNames: booked.langNames,
            unitPrice: booked.unitPrice,
            isPaymentCompulsory: false,
            quantity: booked.hasQuantity ?? (booked.quantity !== undefined && booked.quantity > 0),
            createdAt: '',
            updatedAt: '',
        }));
    const displayServices = [...visibleServices, ...unmatchedBookedServices];
    const hasAnyVisibleService = displayServices.length > 0;
    const mandatoryServices = displayServices.filter((s) => s.isPaymentCompulsory === true);
    const optionalServices = displayServices.filter((s) => !s.isPaymentCompulsory);

    // Mandatory services are always part of the booking, so they always render as selected.
    const renderItem = (service: AdditionalService, forceChecked = false) => {
        const merged = checkHasQuantity(service, additionalServices);
        // `merged.unitPrice` is the booked price for a booked line and the
        // catalog price otherwise; `service.unitPrice` is always the catalog's.
        // For the pseudo-entries above the two are the same value, so an
        // uncatalogued service correctly shows no hint (#1735).
        return (
            <BookingDetailsServiceItem
                isQuantitySelectable={service.quantity}
                service={merged}
                isChecked={forceChecked || merged.isChecked!}
                currentPrice={service.unitPrice}
            />
        );
    };

    return (
        <Box>
            {!hasAnyVisibleService && (
                <Typography color={'text.secondary'} className={'text-sm'}>
                    {t('create_booking.additional_services.empty_state')}
                </Typography>
            )}
            {hasAnyVisibleService && (
                <>
                    <Typography variant={'subtitle1'} className={'font-medium mt-2 mb-1'}>
                        {t('create_booking.additional_services.mandatory_title')}
                    </Typography>
                    {mandatoryServices.length === 0 ? (
                        <Typography color={'text.secondary'} className={'text-sm mb-2'}>
                            {t('create_booking.additional_services.empty_mandatory')}
                        </Typography>
                    ) : (
                        <Grid container spacing={1} className={'mb-2'}>
                            {mandatoryServices.map((service) => (
                                <Grid item xs={12} sm={6} key={service.id}>
                                    {renderItem(service, true)}
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    <Typography variant={'subtitle1'} className={'font-medium mt-3 mb-1'}>
                        {t('create_booking.additional_services.optional_title')}
                    </Typography>
                    {optionalServices.length === 0 ? (
                        <Typography color={'text.secondary'} className={'text-sm'}>
                            {t('create_booking.additional_services.empty_optional')}
                        </Typography>
                    ) : (
                        <Grid container spacing={1}>
                            {optionalServices.map((service) => (
                                <Grid item xs={12} sm={6} key={service.id}>
                                    {renderItem(service)}
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            )}
        </Box>
    );
};

export default BookingDetailsAdditionalServices;
